/**
 * Shared Zero Trust constants — single source of truth for both
 * the internal ZT framework and the sandbox.
 *
 * Any change here is immediately reflected in both systems when
 * sandbox sync mode is set to "auto". In developer mode, changes
 * are buffered until the admin publishes to sandbox.
 */

export const MATURITY_LEVELS = [
  {
    key: 'TRADITIONAL',
    label: 'Traditional',
    short: 'TRAD',
    score: 1,
    description: 'Manual processes, perimeter-based security, limited automation',
    color: '#6b7280',
    cssVar: 'var(--zt-traditional, #6b7280)',
  },
  {
    key: 'INITIAL',
    label: 'Initial',
    short: 'INIT',
    score: 2,
    description: 'Some automation and policy enforcement, beginning identity-centric controls',
    color: '#eab308',
    cssVar: 'var(--zt-initial, #eab308)',
  },
  {
    key: 'ADVANCED',
    label: 'Advanced',
    short: 'ADV',
    score: 3,
    description: 'Integrated controls across pillars, automated policy enforcement',
    color: '#3b82f6',
    cssVar: 'var(--zt-advanced, #3b82f6)',
  },
  {
    key: 'OPTIMAL',
    label: 'Optimal',
    short: 'OPT',
    score: 4,
    description: 'Full Zero Trust implementation, AI-driven policy, continuous verification',
    color: '#10b981',
    cssVar: 'var(--zt-optimal, #10b981)',
  },
];

export const MATURITY_KEYS = MATURITY_LEVELS.map(m => m.key);

export const MATURITY_COLORS = Object.fromEntries(
  MATURITY_LEVELS.map(m => [m.key, m.color])
);

export const MATURITY_SCORES = Object.fromEntries(
  MATURITY_LEVELS.map(m => [m.key, m.score])
);

export const MATURITY_LABELS = Object.fromEntries(
  MATURITY_LEVELS.map(m => [m.score, m.key])
);

export const STATUS_OPTIONS = [
  { key: 'NOT_STARTED', label: 'Not Started' },
  { key: 'PLANNING', label: 'Planning' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'DEPLOYED', label: 'Deployed' },
];

export const STATUS_KEYS = STATUS_OPTIONS.map(s => s.key);

export const PRIORITY_OPTIONS = [
  { key: 'CRITICAL', label: 'Critical', color: '#ef4444' },
  { key: 'HIGH', label: 'High', color: '#f97316' },
  { key: 'MEDIUM', label: 'Medium', color: '#eab308' },
  { key: 'LOW', label: 'Low', color: '#6b7280' },
];

export const PRIORITY_KEYS = PRIORITY_OPTIONS.map(p => p.key);

export const PILLAR_DEFINITIONS = [
  { code: 'IDENTITY', name: 'Identity', icon: '🔑', color: '#6366f1', cssVar: 'var(--zt-identity, #6366f1)' },
  { code: 'DEVICE', name: 'Device', icon: '💻', color: '#8b5cf6', cssVar: 'var(--zt-device, #8b5cf6)' },
  { code: 'NETWORK', name: 'Network', icon: '🌐', color: '#06b6d4', cssVar: 'var(--zt-network, #06b6d4)' },
  { code: 'APPLICATION', name: 'Application', icon: '📱', color: '#10b981', cssVar: 'var(--zt-application, #10b981)' },
  { code: 'DATA', name: 'Data', icon: '🗄️', color: '#f59e0b', cssVar: 'var(--zt-data, #f59e0b)' },
  { code: 'VISIBILITY', name: 'Visibility & Analytics', icon: '👁️', color: '#ec4899', cssVar: 'var(--zt-visibility, #ec4899)' },
];

export const PILLAR_CODES = PILLAR_DEFINITIONS.map(p => p.code);

export const PILLAR_NAMES = Object.fromEntries(
  PILLAR_DEFINITIONS.map(p => [p.code, p.name])
);

/**
 * Default gap templates per pillar — used by sandbox when no
 * published framework config exists. Internal ZT uses real
 * capabilities from the backend; these are the simplified version.
 */
export const DEFAULT_GAP_TEMPLATES = {
  IDENTITY: [
    { capability: 'Multi-Factor Authentication', description: 'MFA not enforced across all user accounts' },
    { capability: 'Privileged Access Management', description: 'No PAM solution for administrative accounts' },
    { capability: 'Identity Governance', description: 'Manual user lifecycle management creates security gaps' },
  ],
  DEVICE: [
    { capability: 'Endpoint Detection & Response', description: 'No EDR solution deployed across managed devices' },
    { capability: 'Device Compliance', description: 'Device health checks not enforced before granting access' },
    { capability: 'Patch Management', description: 'Inconsistent patching cadence creates vulnerability windows' },
  ],
  NETWORK: [
    { capability: 'Micro-Segmentation', description: 'Flat network topology without proper segmentation' },
    { capability: 'Encrypted Communications', description: 'Not all internal traffic is encrypted in transit' },
    { capability: 'Zero Trust Network Access', description: 'No ZTNA solution replacing traditional VPN' },
  ],
  APPLICATION: [
    { capability: 'Application Security Testing', description: 'No SAST/DAST pipeline for application code' },
    { capability: 'API Security', description: 'API endpoints lack proper authentication and rate limiting' },
    { capability: 'Application Access Control', description: 'Application access not tied to identity context' },
  ],
  DATA: [
    { capability: 'Data Classification', description: 'No automated data classification or labeling system' },
    { capability: 'Data Loss Prevention', description: 'No DLP controls for sensitive data exfiltration' },
    { capability: 'Encryption at Rest', description: 'Not all data stores use encryption at rest' },
  ],
  VISIBILITY: [
    { capability: 'SIEM/SOAR', description: 'No centralized security event monitoring or automated response' },
    { capability: 'Threat Intelligence', description: 'No threat intelligence feeds integrated into detection' },
    { capability: 'Incident Response Plan', description: 'IR plan not tested or updated regularly' },
  ],
};

/**
 * The localStorage key for the published sandbox config.
 * This is what the sandbox reads on load.
 */
export const SANDBOX_CONFIG_KEY = 'zt_sandbox_published_config';
export const SANDBOX_SYNC_PREFS_KEY = 'zt_sandbox_sync_prefs';
